import React from "react"
import PropTypes from "prop-types"
import Helmet from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import favicon32 from "../images/favicon32.png"
import favicon16 from "../images/favicon16.png"
import favicon64 from "../images/favicon64.png"
import socialBanner from "../images/metaImage.jpg"
import logo from "../images/Face-institute-logo.png"
import callumPortrait from "../images/callum-faris-portrait.webp"
import susanPortrait from "../images/susan-schmidt-portrait.webp"

// `medicalPage`: optional per-page medical attribution. When passed, the page
// emits a MedicalWebPage node with a `lastReviewed` date and a `reviewedBy`
// reference to one of the clinic's Physician entities. Shape:
//   { reviewer: "callum" | "susan", lastReviewed: "YYYY-MM-DD", procedure?: "Rhinoplasty" }
function SEO({
  description,
  lang,
  meta,
  title,
  image,
  path,
  xDefaultLocale,
  medicalPage,
}) {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
          image
          siteUrl
        }
      }
    }
  `)

  const metaDescription = description || site.siteMetadata.description
  const siteUrl = site.siteMetadata.siteUrl

  // Turn a webpack asset path (root-relative) into an absolute URL for OG/JSON-LD.
  const toAbsolute = (src) =>
    !src || src.startsWith("http") || src.startsWith("data:")
      ? src
      : `${siteUrl}${src}`

  // SELF-REFERENCING canonical. Dutch is served at the root, English under /en/.
  // The canonical always points at the page's OWN language URL — it never points a
  // Dutch URL at an English one (or vice-versa), so it cannot collapse the languages.
  // A /nl/ prefix (duplicate of the root version), if ever present, folds back to root.
  const normalizedPath = path.startsWith("/nl/") ? path.substring(3) : path
  const canonical = `${siteUrl}${normalizedPath}`

  const ogImage = toAbsolute(image || socialBanner)

  // hreflang alternates are built from the language-neutral base path, so a
  // page visited under either prefix maps to the same pair of URLs. Dutch
  // lives at the root (matching the canonical above — a /nl/ prefix folds
  // back to root), English under /en/.
  const basePath =
    path.startsWith("/en/") || path.startsWith("/nl/")
      ? path.substring(3)
      : path
  const dutchHref = `${siteUrl}${basePath}`
  const englishHref = `${siteUrl}/en${basePath}`
  // x-default normally follows Dutch (the site's primary market). A page can opt
  // into English as its x-default via `xDefaultLocale="en"` — e.g. the
  // international-patients page, whose audience is English-speaking.
  const generateHreflangLinks = [
    { hreflang: "nl", href: dutchHref }, // Ensure Dutch alternate
    { hreflang: "en", href: englishHref }, // English alternate
    {
      hreflang: "x-default",
      href: xDefaultLocale === "en" ? englishHref : dutchHref,
    },
  ].map(({ hreflang, href }) => ({
    rel: "alternate",
    hreflang,
    href,
  }))

  // Sitewide structured data: the clinic organization, its physical locations,
  // and the website entity. Same on every page (standard for org-level schema).
  const orgId = `${siteUrl}/#organization`
  const callumId = `${siteUrl}/#physician-callum-faris`
  const susanId = `${siteUrl}/#physician-susan-schmidt`

  // The two treating physicians as first-class entities, linked to the
  // organization via worksFor/employee. Credentials, BIG registrations,
  // memberships and education are what carry E-E-A-T for YMYL medical content.
  const physicians = [
    {
      "@type": "Physician",
      "@id": callumId,
      name: "Dr Callum Faris",
      honorificPrefix: "Dr",
      givenName: "Callum",
      familyName: "Faris",
      jobTitle: "ENT specialist & facial plastic surgeon",
      image: toAbsolute(callumPortrait),
      url: `${siteUrl}/about/`,
      worksFor: { "@id": orgId },
      medicalSpecialty: ["Otolaryngologic", "PlasticSurgery"],
      // "Facelift" and "deep plane facelift" are used interchangeably in search;
      // list both so the entity matches the generic query as well as the technique.
      knowsAbout: [
        "Facelift and neck lift",
        "Deep plane facelift",
        "Rhinoplasty and rhinoplasty revision",
        "Revision (secondary) facelift",
        "Facial reconstruction",
        "Facial paralysis correction",
      ],
      memberOf: {
        "@type": "MedicalOrganization",
        name: "European Academy of Facial Plastic Surgery (EAFPS)",
      },
      alumniOf: [
        {
          "@type": "CollegeOrUniversity",
          name: "St Bartholomew's Medical School, University of London",
        },
        {
          "@type": "MedicalOrganization",
          name: "Harvard Medical School — Massachusetts Eye and Ear",
        },
      ],
      identifier: {
        "@type": "PropertyValue",
        propertyID: "BIG-register",
        name: "BIG registration",
        value: "19921058501",
      },
      sameAs: [
        "https://www.instagram.com/doctor.callum.faris/",
        "https://www.realself.com/dr/callum-faris-amsterdam-netherlands",
        // TODO: add Dr Faris's Google Scholar profile URL once he activates it,
        // e.g. "https://scholar.google.com/citations?user=XXXXXXXX"
      ],
    },
    {
      "@type": "Physician",
      "@id": susanId,
      name: "Dr Susan Schmidt",
      honorificPrefix: "Dr",
      givenName: "Susan",
      familyName: "Schmidt",
      jobTitle: "M.D. Cosmetic Doctor KNMG",
      image: toAbsolute(susanPortrait),
      url: `${siteUrl}/about/`,
      worksFor: { "@id": orgId },
      knowsAbout: [
        "General practice medicine (over 20 years as a GP)",
        "Injectables",
        "Botulinum toxin (btx)",
        "Dermal fillers",
        "Facial 3D treatments",
        "Chemical skin peels",
        "Managing injectable complications",
      ],
      memberOf: {
        "@type": "MedicalOrganization",
        name: "Nederlandse Vereniging Cosmetische Geneeskunde (NVCG)",
        url: "https://nvcg.nl",
      },
      alumniOf: {
        "@type": "CollegeOrUniversity",
        name: "University of Helsinki",
        sameAs: "https://www.helsinki.fi/en",
      },
      identifier: {
        "@type": "PropertyValue",
        propertyID: "BIG-register",
        name: "BIG registration",
        value: "19046273501",
      },
      sameAs: ["https://www.instagram.com/drsusanschmidt/"],
    },
  ]

  const clinicLocations = [
    {
      "@type": "MedicalClinic",
      "@id": `${siteUrl}/#clinic-amsterdam-frederiksplein`,
      name: "Face Institute — Amsterdam (Frederiksplein)",
      parentOrganization: { "@id": orgId },
      url: `${siteUrl}/`,
      image: ogImage,
      telephone: "+31850290829",
      email: "info@faceinstitute.nl",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Frederiksplein 47B",
        postalCode: "1017 XL",
        addressLocality: "Amsterdam",
        addressCountry: "NL",
      },
    },
    {
      "@type": "MedicalClinic",
      "@id": `${siteUrl}/#clinic-bergen`,
      name: "Face Institute — Bergen",
      parentOrganization: { "@id": orgId },
      url: `${siteUrl}/`,
      image: ogImage,
      telephone: "+31850290829",
      email: "info@faceinstitute.nl",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Dokter van Peltlaan 5",
        postalCode: "1861 KA",
        addressLocality: "Bergen",
        addressCountry: "NL",
      },
    },
  ]

  const graph = [
    {
      "@type": "MedicalOrganization",
      "@id": orgId,
      name: "Face Institute",
      url: `${siteUrl}/`,
      logo: toAbsolute(logo),
      image: ogImage,
      description: site.siteMetadata.description,
      email: "info@faceinstitute.nl",
      telephone: "+31850290829",
      medicalSpecialty: ["PlasticSurgery", "Otolaryngologic"],
      founder: { "@id": callumId },
      employee: [{ "@id": callumId }, { "@id": susanId }],
      sameAs: [
        "https://www.facebook.com/faceinstitute.nl",
        "https://www.instagram.com/faceinstitute.nl/",
      ],
    },
    ...physicians,
    ...clinicLocations,
    {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      url: `${siteUrl}/`,
      name: "Face Institute",
      publisher: { "@id": orgId },
      inLanguage: lang === "en" ? "en" : "nl",
    },
  ]

  // Per-page medical review attribution (opt-in via the `medicalPage` prop).
  if (medicalPage && medicalPage.lastReviewed) {
    const reviewerId = medicalPage.reviewer === "susan" ? susanId : callumId
    graph.push({
      "@type": "MedicalWebPage",
      "@id": `${canonical}#webpage`,
      url: canonical,
      name: title,
      inLanguage: lang === "en" ? "en" : "nl",
      isPartOf: { "@id": `${siteUrl}/#website` },
      publisher: { "@id": orgId },
      lastReviewed: medicalPage.lastReviewed,
      reviewedBy: { "@id": reviewerId },
      ...(medicalPage.procedure
        ? { about: { "@type": "MedicalProcedure", name: medicalPage.procedure } }
        : {}),
    })
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": graph,
  }

  return (
    <Helmet
      htmlAttributes={{ lang }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      meta={[
        { name: "description", content: metaDescription },
        { property: "og:title", content: title },
        { property: "og:description", content: metaDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: ogImage },
        { property: "og:site_name", content: site.siteMetadata.title },
        { property: "og:locale", content: lang === "en" ? "en_US" : "nl_NL" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: metaDescription },
        { name: "twitter:image", content: ogImage },
        { name: "image", content: ogImage },
      ].concat(meta)}
      link={[
        { rel: "canonical", href: canonical },
        { rel: "icon", type: "image/png", sizes: "16x16", href: favicon16 },
        { rel: "icon", type: "image/png", sizes: "32x32", href: favicon32 },
        { rel: "shortcut icon", type: "image/png", href: favicon64 },
        ...generateHreflangLinks,
      ]}
    >
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )
}

SEO.defaultProps = {
  // Dutch is the site's default language and primary market — fall back to it
  // (not English) when a page doesn't pass `lang` explicitly.
  lang: "nl",
  meta: [],
  description: "",
  // Site-wide default: x-default follows Dutch. Pages override per-page.
  xDefaultLocale: "nl",
  medicalPage: null,
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  xDefaultLocale: PropTypes.oneOf(["nl", "en"]),
  medicalPage: PropTypes.shape({
    reviewer: PropTypes.oneOf(["callum", "susan"]),
    lastReviewed: PropTypes.string,
    procedure: PropTypes.string,
  }),
}

export default SEO
